<template>
  <div
    v-if="visible"
    class="donor_modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="donor-modal-title"
    @click.self="dismiss"
  >
    <div class="donor_modal_inner">
      <button class="donor_modal_close" type="button" aria-label="Close" @click="dismiss">
        &times;
      </button>

      <span class="donor_modal_badge">Donor funded &middot; 150 subscriptions</span>

      <h3 id="donor-modal-title" class="donor_modal_title">
        FREE <span class="orange">MyPBE Pro</span> Opportunity
      </h3>

      <p class="donor_modal_lead">
        A donor has graciously provided <strong>150 MyPBE Pro subscriptions</strong>
        for the <span class="purple">2026&ndash;2027</span> PBE season! Earn
        <strong>50 activity points before October&nbsp;1</strong> and one is yours.
      </p>

      <ol class="donor_modal_steps">
        <li>
          <span class="donor_step_num">1</span>
          <span class="donor_step_body">
            <strong>Setup your practice chapters.</strong>
            Chapters are setup under the &ldquo;Chapters&rdquo; tab on the
            &ldquo;My Profile&rdquo; page.
          </span>
        </li>
        <li>
          <span class="donor_step_num">2</span>
          <span class="donor_step_body">
            <strong>Earn at least 50 activity points before October&nbsp;1.</strong>
            Activity points are earned for completing Chapter Drills, Lightning
            Challenges and Assessments.
          </span>
        </li>
        <li>
          <span class="donor_step_num">3</span>
          <span class="donor_step_body">
            <strong>Keep MyPBE Pro through the end of the season.</strong>
            There&rsquo;s nothing to apply for. The subscription is granted the
            moment you cross the threshold.
          </span>
        </li>
      </ol>

      <p class="donor_modal_note">
        First come, first served: once the 150 subscriptions are claimed, the offer
        closes. Tell your friends!
      </p>

      <div class="donor_modal_actions">
        <a class="donor_modal_cta" href="https://app.mypbe.com/register" @click="dismiss">
          Start earning points
        </a>
        <button class="donor_modal_dismiss" type="button" @click="dismiss">
          Maybe later
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  donorProAccess,
  dismissDonorProAccess,
  isDonorProAccessOfferOpen,
  openDonorProAccess,
} from "@/donorProAccess";

/**
 * Announces the donor-funded Pro seats for the 2026-2027 season.
 *
 * Opens itself once per visitor; after that the header's "Free Pro" item is the
 * way back in. Both share `donorProAccess`, which also owns the dismissal.
 */

/**
 * The preloader is removed at 2000ms (see `utilits.aali_tm_preloader`), so the
 * modal waits it out rather than opening behind it.
 */
const REVEAL_DELAY_MS = 2200;

export default {
  name: "DonorProAccessModal",
  data() {
    return { state: donorProAccess, timer: null };
  },
  computed: {
    visible() {
      return this.state.open;
    },
  },
  watch: {
    /**
     * The header can open this too, so Escape follows the state rather than the
     * single call site that used to own the listener.
     */
    visible(open) {
      if (open) document.addEventListener("keydown", this.onKeydown);
      else document.removeEventListener("keydown", this.onKeydown);
    },
  },
  mounted() {
    this.state.mounted = true;

    if (!isDonorProAccessOfferOpen() || this.state.dismissed) return;

    this.timer = window.setTimeout(openDonorProAccess, REVEAL_DELAY_MS);
  },
  beforeUnmount() {
    if (this.timer) window.clearTimeout(this.timer);
    document.removeEventListener("keydown", this.onKeydown);

    this.state.mounted = false;
    // Leaving this set would reopen the modal the next time one mounts.
    this.state.open = false;
  },
  methods: {
    dismiss() {
      dismissDonorProAccess();
    },
    onKeydown(event) {
      if (event.key === "Escape") this.dismiss();
    },
  },
};
</script>

<style>
.donor_modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background-color: rgba(20, 16, 32, 0.72);
  backdrop-filter: blur(4px);
  overflow-y: auto;
}

.donor_modal_inner {
  position: relative;
  width: 100%;
  max-width: 620px;
  margin: auto;
  padding: 40px;
  border-radius: 20px;
  /* Opaque stops on purpose: a translucent panel let the blurred hero behind
     the overlay wash through the badge and title. These are the old rgba tints
     already composited against the panel colour, so the warm top reads the
     same without the show-through. */
  background: linear-gradient(180deg, #fff2ec 0%, #ffffff 45%, #ffffff 100%);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.donor_modal_close {
  position: absolute;
  top: 14px;
  right: 16px;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  color: #6f6b80;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.donor_modal_close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #2c2c2c;
}

.donor_modal_badge {
  display: inline-block;
  margin-bottom: 14px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(28, 190, 89, 0.14);
  color: #148a40;
  font-family: "Jost", sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.donor_modal_title {
  margin: 0 0 14px;
  font-family: "Jost", sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.25;
  color: #2c2c2c;
}

.donor_modal_title .orange,
.donor_modal_lead .orange {
  color: #f75023;
}

.donor_modal_title .purple,
.donor_modal_lead .purple {
  color: #8067f0;
  font-weight: 600;
}

.donor_modal_lead {
  margin: 0 0 24px;
  font-size: 16px;
  line-height: 1.7;
  color: #6f6b80;
}

.donor_modal_lead strong {
  color: #2c2c2c;
}

.donor_modal_steps {
  margin: 0 0 20px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.donor_modal_steps li {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.donor_step_num {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(250, 91, 15, 0.12);
  color: #f75023;
  font-family: "Jost", sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.donor_step_body {
  font-size: 15px;
  line-height: 1.6;
  color: #6f6b80;
}

.donor_step_body strong {
  color: #2c2c2c;
}

.donor_modal_note {
  margin: 0 0 26px;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(128, 103, 240, 0.08);
  font-size: 14px;
  line-height: 1.6;
  color: #5b4fa8;
}

.donor_modal_actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.donor_modal_cta {
  display: inline-block;
  padding: 13px 26px;
  border-radius: 999px;
  background-color: #f75023;
  color: #fff;
  font-family: "Jost", sans-serif;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.donor_modal_cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(247, 80, 35, 0.32);
}

.donor_modal_dismiss {
  padding: 13px 20px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #6f6b80;
  font-family: "Jost", sans-serif;
  font-size: 15px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.donor_modal_dismiss:hover {
  color: #2c2c2c;
}

/* Unscoped, like the other landing components: the dark theme is toggled by a
   `dark` class on <body>, which a scoped stylesheet cannot select as an
   ancestor. Every selector here is prefixed with the component's own
   `donor_modal` names so nothing leaks. */
body.dark .donor_modal_inner {
  background: linear-gradient(180deg, #3d2327 0%, #1e1a2b 45%, #191527 100%);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark .donor_modal_title,
body.dark .donor_modal_lead strong,
body.dark .donor_step_body strong {
  color: #f2f0f7;
}

body.dark .donor_modal_title .purple,
body.dark .donor_modal_lead .purple {
  color: #a494ff;
}

body.dark .donor_modal_lead,
body.dark .donor_step_body {
  color: #b9b4c9;
}

body.dark .donor_modal_badge {
  background: rgba(28, 190, 89, 0.2);
  color: #7ee0a4;
}

body.dark .donor_modal_note {
  background: rgba(128, 103, 240, 0.16);
  color: #c3b6ff;
}

body.dark .donor_modal_close {
  background: rgba(255, 255, 255, 0.08);
  color: #b9b4c9;
}

body.dark .donor_modal_close:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

body.dark .donor_modal_dismiss {
  color: #b9b4c9;
}

body.dark .donor_modal_dismiss:hover {
  color: #fff;
}

/* Opening is animated with keyframes rather than a Vue <transition>: a leave
   transition only finishes once Vue applies its `leave-to` class on the next
   animation frame, and a throttled tab never delivers that frame — which would
   strand this overlay on screen after the user dismissed it. `v-if` alone is
   unconditional. */
@keyframes donor_modal_fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes donor_modal_rise {
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to { opacity: 1; transform: none; }
}

.donor_modal {
  animation: donor_modal_fade 0.3s ease both;
}

.donor_modal_inner {
  animation: donor_modal_rise 0.3s ease both;
}

@media (prefers-reduced-motion: reduce) {
  .donor_modal,
  .donor_modal_inner {
    animation: none;
  }
}

@media (max-width: 600px) {
  .donor_modal {
    padding: 16px 12px;
  }

  .donor_modal_inner {
    padding: 30px 20px 24px;
    border-radius: 16px;
  }

  .donor_modal_title {
    font-size: 24px;
  }

  .donor_modal_lead {
    font-size: 15px;
  }

  .donor_modal_actions {
    flex-direction: column;
    align-items: stretch;
  }

  .donor_modal_cta,
  .donor_modal_dismiss {
    text-align: center;
  }
}
</style>
